import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Loader2, Mail, Send, X } from 'lucide-react';
import type { OrgInvitation } from '../types/org-users.types';

interface PendingInvitationsProps {
  invitations: OrgInvitation[];
  isLoading?: boolean;
  onResend: (invitation: OrgInvitation) => void;
  onRevoke: (invitation: OrgInvitation) => void;
  resendingId: string | null;
}

function getRelativeTime(timestamp: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return t('ORGTEAM_PENDINGINVITATIONS_TIME_DAYS', { count: days });
  }
  if (hours > 0) {
    return t('ORGTEAM_PENDINGINVITATIONS_TIME_HOURS', { count: hours });
  }
  if (minutes > 0) {
    return t('ORGTEAM_PENDINGINVITATIONS_TIME_MINUTES', { count: minutes });
  }
  return t('ORGTEAM_PENDINGINVITATIONS_TIME_JUSTNOW');
}

export const PendingInvitations = ({
  invitations,
  isLoading,
  onResend,
  onRevoke,
  resendingId,
}: PendingInvitationsProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="size-5" />
            {t('ORGTEAM_PENDINGINVITATIONS_TITLE')}
          </CardTitle>
          <CardDescription>
            {t('ORGTEAM_PENDINGINVITATIONS_SUBTITLE')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-4 w-24" />
                <div className="ml-auto flex gap-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="size-5" />
          {t('ORGTEAM_PENDINGINVITATIONS_TITLEWITHCOUNT', {
            count: invitations.length,
          })}
        </CardTitle>
        <CardDescription>
          {t('ORGTEAM_PENDINGINVITATIONS_SUBTITLE')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border border-secondary-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted border-secondary-100">
                <TableHead>
                  {t('ORGTEAM_PENDINGINVITATIONS_COLUMNS_EMAIL')}
                </TableHead>
                <TableHead>
                  {t('ORGTEAM_PENDINGINVITATIONS_COLUMNS_ROLE')}
                </TableHead>
                <TableHead>
                  {t('ORGTEAM_PENDINGINVITATIONS_COLUMNS_SENT')}
                </TableHead>
                <TableHead className="text-right">
                  {t('ORGTEAM_PENDINGINVITATIONS_COLUMNS_ACTIONS')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => {
                const isResending = resendingId === invitation.id;
                const roleKey =
                  invitation.role?.replace('org_', '') || 'member';
                return (
                  <TableRow
                    key={invitation.id}
                    className="hover:bg-[hsl(var(--muted))]/50 border-b border-secondary-100"
                  >
                    <TableCell className="font-medium">
                      {invitation.email}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {t(`ORGTEAM_ROLES_${roleKey.toUpperCase()}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-globe-grey">
                      {getRelativeTime(invitation.createdAt, t)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onResend(invitation)}
                          disabled={isResending}
                          className="text-selise-blue hover:text-selise-blue hover:bg-selise-blue/10"
                        >
                          {isResending ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="size-4 mr-1" />
                              {t('ORGTEAM_PENDINGINVITATIONS_ACTIONS_RESEND')}
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRevoke(invitation)}
                          disabled={isResending}
                          className="text-globe-grey hover:text-crimson hover:bg-[hsl(var(--crimson))]/10"
                        >
                          <X className="size-4 mr-1" />
                          {t('ORGTEAM_PENDINGINVITATIONS_ACTIONS_REVOKE')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
